/**
 * useLibrary, React state over the recents + bookmarks store (`./library.ts`).
 * One instance lives in AppNext; it hands recents/bookmarks to the home and a
 * bookmark toggle to the report.
 *
 * Every setter is fed the list the STORE returns after its write, never a
 * list computed here. The store owns the caps, the dedupe and the legacy-key
 * migration, so a locally assembled array would drift from what is on disk
 * as soon as any of those changed. Read once on mount: this deliberately
 * does not follow another tab's writes, which is why the recents on the home
 * screen are a snapshot of the session, not a live feed.
 *
 * That rule is why the two bookmark setters need no branch for a refused
 * write: the store returns the PREVIOUS list when the write did not land, so
 * feeding it in unconditionally leaves the star exactly where the device says
 * it should be. The `BookmarkWrite` is handed back to the caller so it can say
 * out loud that nothing was saved, which the star alone cannot.
 */
import { useCallback, useState } from "react";
import {
  addRecent,
  clearRecents,
  getBookmarks,
  getRecents,
  removeBookmark,
  toggleBookmark,
  type BookmarkWrite,
  type LibEntry,
} from "./library";

export function useLibrary() {
  const [recents, setRecents] = useState<LibEntry[]>(() => getRecents());
  const [bookmarks, setBookmarks] = useState<LibEntry[]>(() => getBookmarks());

  const remember = useCallback((e: LibEntry) => setRecents(addRecent(e)), []);
  const toggle = useCallback((e: LibEntry): BookmarkWrite => {
    const written = toggleBookmark(e);
    setBookmarks(written.list);
    return written;
  }, []);
  const unbookmark = useCallback((id: string): BookmarkWrite => {
    const written = removeBookmark(id);
    setBookmarks(written.list);
    return written;
  }, []);
  const clearRecent = useCallback(() => setRecents(clearRecents()), []);

  return { recents, bookmarks, remember, toggle, unbookmark, clearRecent };
}
